import { useEffect, useMemo, useState } from "react";
import { axios } from "helper/axios";
import {
  getCacheEpoch,
  getCachedOffer,
  saveCachedOffer,
} from "helper/dashboardOfferCache";

const OFFERS_API = "dashboard/offers";

const EMPTY_OFFER = {
  offerShow: false,
  offerName: null,
  discountOffer: null,
  discountLabel: null,
  endDate: null,
  endsAt: null,
  essentialsBgWeb: null,
  essentialsBgMobile: null,
  essentialsTitleColor: null,
  essentialsSubtitle: null,
};

// Campaign fields for the Investing Essentials card. Every offer (Diwali,
// December, ...) is configured by the backend; when the image URLs are absent
// the dashboard shows the normal card.
const readTrimmedString = (value) =>
  typeof value === "string" && value.trim() ? value.trim() : null;

// A colour the browser can't parse (e.g. "golden") is dropped, so the title
// falls back to the colour measured from the image instead of breaking.
const readCssColor = (value) => {
  const color = readTrimmedString(value);
  if (!color) return null;
  try {
    return window.CSS?.supports?.("color", color) === false ? null : color;
  } catch (error) {
    return color;
  }
};

// Accepts true / "true" / 1 / "1" so a stringly-typed flag can't hide an offer.
const readFlag = (value) =>
  value === true ||
  value === 1 ||
  (typeof value === "string" && ["true", "1"].includes(value.trim().toLowerCase()));

// The offer runs until the END of `endDate` in India time. Dates without a
// time zone are read as IST, and every format is normalised to ISO first:
// Safari (the iOS app) rejects "YYYY-MM-DD HH:mm:ss", and no browser reads
// "DD-MM-YYYY", either of which would otherwise hide the offer silently.
const IST_OFFSET = "+05:30";
const parseEndDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const raw = String(value).trim();

  let iso = raw;
  let match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    iso = `${raw}T23:59:59${IST_OFFSET}`;
  } else if ((match = raw.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/))) {
    const [, day, month, year] = match;
    iso = `${year}-${month}-${day}T23:59:59${IST_OFFSET}`;
  } else if (
    (match = raw.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(:\d{2})?(\.\d+)?$/))
  ) {
    const [, date, time, seconds = ":00"] = match;
    iso = `${date}T${time}${seconds}${IST_OFFSET}`;
  } else if ((match = raw.match(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}.*)$/))) {
    iso = `${match[1]}T${match[2]}`;
  }

  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
};

const parseOfferResponse = (response) => {
  const data = response?.data ?? response ?? {};
  const offerShow = readFlag(data.OfferShow) || readFlag(data.offerShow);
  const offerName = data.OfferName ?? data.offerName ?? null;
  const discountOffer = data.DiscountOffer ?? data.discountOffer ?? null;
  const endDate = data.endDate ?? data.EndDate ?? null;

  let discountLabel = null;
  if (discountOffer) {
    const raw = String(discountOffer).trim();
    discountLabel = /discount/i.test(raw) ? raw : `${raw} discount`;
  }

  const endsAt = parseEndDate(endDate);

  return {
    offerShow,
    offerName,
    discountOffer,
    discountLabel,
    endDate,
    endsAt,
    essentialsBgWeb: readTrimmedString(
      data.essentialsBgWeb ?? data.EssentialsBgWeb,
    ),
    essentialsBgMobile: readTrimmedString(
      data.essentialsBgMobile ?? data.EssentialsBgMobile,
    ),
    essentialsTitleColor: readCssColor(
      data.essentialsTitleColor ?? data.EssentialsTitleColor,
    ),
    essentialsSubtitle: readTrimmedString(
      data.essentialsSubtitle ?? data.EssentialsSubtitle,
    ),
  };
};

// One request shared by every mounted caller; each response refreshes the
// cache (unless the cache was cleared by a logout meanwhile).
let offerRequest = null;
const fetchOffer = () => {
  if (!offerRequest) {
    const epoch = getCacheEpoch();
    offerRequest = axios
      .get(OFFERS_API, { skipErrorToast: true })
      .then((response) => {
        const fresh = parseOfferResponse(response);
        if (getCacheEpoch() === epoch) {
          saveCachedOffer(fresh);
        }
        return fresh;
      })
      .finally(() => {
        offerRequest = null;
      });
  }
  return offerRequest;
};

const readCachedOffer = () => {
  const cached = getCachedOffer();
  return cached ? { ...EMPTY_OFFER, ...cached } : null;
};

const isSameOffer = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Stale-while-revalidate: start from the last known offer so the card renders
// at once, then refresh from the API without making the screen wait for it.
export const useDashboardOffer = () => {
  const [offer, setOffer] = useState(() => readCachedOffer() ?? EMPTY_OFFER);
  const [loading, setLoading] = useState(() => !getCachedOffer());

  useEffect(() => {
    let cancelled = false;

    fetchOffer()
      .then((fresh) => {
        if (!cancelled) {
          setOffer((current) => (isSameOffer(current, fresh) ? current : fresh));
        }
      })
      .catch((error) => {
        // Keep showing the last known offer; it still ends at its endDate.
        // Without one the card simply stays normal.
        console.error("Failed to load dashboard offers:", error);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const isActive = useMemo(() => {
    if (!offer.offerShow || !offer.endsAt) return false;
    return offer.endsAt > Date.now();
  }, [offer.offerShow, offer.endsAt]);

  return {
    offer,
    isActive,
    loading,
  };
};

export default useDashboardOffer;
